/**
 * Reusable Error Boundary
 * Catches rendering errors in child components and displays a fallback UI.
 * Use at route, feature, or component level to prevent cascading failures.
 *
 * Usage:
 *   <ErrorBoundary feature="Investment Analysis">
 *     <InvestmentPanel />
 *   </ErrorBoundary>
 */

import React, { Component } from 'react';
import { Box, Alert, AlertTitle, Button, Typography, Collapse } from '@mui/material';
import { Refresh, ExpandMore, ExpandLess } from '@mui/icons-material';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Feature name to display in error message */
  feature?: string;
  /** Custom fallback component (overrides default error UI) */
  fallback?: React.ReactNode;
  /** Called when an error is caught */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /** Variant: 'full' shows page-level error, 'inline' shows compact error */
  variant?: 'full' | 'inline';
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  showDetails: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, showDetails: false };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { feature, onError } = this.props;
    console.error(`[ErrorBoundary${feature ? `: ${feature}` : ''}]`, error, errorInfo);
    onError?.(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, showDetails: false });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    const { feature, variant = 'inline' } = this.props;
    const { error, showDetails } = this.state;

    if (variant === 'full') {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', p: 4 }}>
          <Box sx={{ textAlign: 'center', maxWidth: 500 }}>
            <Typography variant="h5" gutterBottom>
              Something went wrong
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {feature
                ? `The ${feature} module encountered an error.`
                : 'An unexpected error occurred.'
              }
            </Typography>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={this.handleReset}
              sx={{ mr: 1 }}
            >
              Try Again
            </Button>
            {error && (
              <>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => this.setState({ showDetails: !showDetails })}
                  endIcon={showDetails ? <ExpandLess /> : <ExpandMore />}
                  sx={{ ml: 1 }}
                >
                  Details
                </Button>
                <Collapse in={showDetails}>
                  <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>
                    <AlertTitle>{error.name}</AlertTitle>
                    <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {error.message}
                    </Typography>
                  </Alert>
                </Collapse>
              </>
            )}
          </Box>
        </Box>
      );
    }

    // Inline variant — compact
    return (
      <Box sx={{ p: 2 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={this.handleReset} startIcon={<Refresh />}>
              Retry
            </Button>
          }
        >
          <AlertTitle>
            {feature ? `${feature} Error` : 'Error'}
          </AlertTitle>
          {error?.message || 'An unexpected error occurred.'}
        </Alert>
      </Box>
    );
  }
}

export default ErrorBoundary;
